// ─── PDF Rapor Servisi ────────────────────────────────────────────────────────
import { clearAuthSession } from '@/store/authStore';

export interface PdfReportDef {
  id: string;
  title: string;
  description: string;
  /** Nginx üzerinden erişilen path, örn. /report-api/powerplants */
  path: string;
  filename: string;
}

export interface ReportOpenOptions {
  investorUuid?: string | null;
  investorName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  rangeLabel?: string | null;
  powerPlantUuids?: string[] | null;
  extraParams?: Record<string, string | number | boolean | null | undefined>;
}

export interface ReportSettingsRecord<T = Record<string, unknown>> {
  report_id: string;
  settings_json: T;
  updated_at: string | null;
}

export interface PlantMonthlyCustomReportSettings {
  primaryColor: string;
  accentColor: string;
  tableHeaderBg: string;
  titleColor: string;
  chartWidthRatio: number;
  chartHeightMm: number;
  titleFontSize: number;
  tableFontSize: number;
  detailFontSize: number;
  summaryFontSize: number;
  gaugeSizeMm: number;
  showGauge: boolean;
}

export const DEFAULT_PLANT_MONTHLY_CUSTOM_REPORT_SETTINGS: PlantMonthlyCustomReportSettings = {
  primaryColor: '#76b852',
  accentColor: '#5ca03e',
  tableHeaderBg: '#eef4ea',
  titleColor: '#111827',
  chartWidthRatio: 0.5,
  chartHeightMm: 72,
  titleFontSize: 13,
  tableFontSize: 6.2,
  detailFontSize: 7.6,
  summaryFontSize: 8.2,
  gaugeSizeMm: 34,
  showGauge: true,
};

export interface ProblematicStringReportRow {
  power_plant_uuid_id: string;
  power_plant_name: string;
  inverter_id: number;
  inverter_name: string;
  string_number: number;
  string_efficiency: number;
}

async function fetchPdfBlobUrlFromPath(path: string, options?: ReportOpenOptions): Promise<string> {
  const token = localStorage.getItem('token');
  const target = new URL(path, window.location.origin);
  if (options?.investorUuid) target.searchParams.set('investor_uuid', options.investorUuid);
  if (options?.investorName) target.searchParams.set('investor_name', options.investorName);
  if (options?.startDate) target.searchParams.set('start_date', options.startDate);
  if (options?.endDate) target.searchParams.set('end_date', options.endDate);
  if (options?.rangeLabel) target.searchParams.set('range_label', options.rangeLabel);
  if (options?.powerPlantUuids?.length) target.searchParams.set('power_plant_uuids', options.powerPlantUuids.join(','));
  if (options?.extraParams) {
    Object.entries(options.extraParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        target.searchParams.set(key, String(value));
      }
    });
  }

  const res = await fetch(target.pathname + target.search, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (res.status === 401) {
    clearAuthSession();
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new Error('Oturum süresi doldu.');
  }
  if (!res.ok) {
    throw new Error(`PDF alınamadı (${res.status})`);
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/pdf')) {
    throw new Error('Rapor servisi şu an erişilemiyor. Lütfen daha sonra tekrar deneyin.');
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Uygulamada tanımlı PDF raporlarının listesi */
export const PDF_REPORTS: PdfReportDef[] = [
  {
    id: 'powerplants',
    title: 'Santral Üretim Raporu',
    description:
      'Seçilen yatırımcı ve tarih aralığına göre santral üretim özeti ile dağılım grafiği.',
    path: '/report-api/powerplants',
    filename: 'santral-uretim-raporu.pdf',
  },
  {
    id: 'problematic-strings-pdf',
    title: 'Sorunlu Stringler Raporu',
    description:
      'String sayfasında kırmızı görünen, verimliliği %50 altındaki stringlerin PDF listesi.',
    path: '/report-api/problematic-strings',
    filename: 'sorunlu-stringler-raporu.pdf',
  },
  {
    id: 'investor-daily',
    title: 'Günlük Yatırımcı Raporu',
    description:
      'Yatırımcı bazında günlük üretim özeti, son 7 gün grafiği ve santral kırılımı.',
    path: '/report-api/investor-daily',
    filename: 'gunluk-yatirimci-raporu.pdf',
  },
  {
    id: 'investor-monthly',
    title: 'Aylık Yatırımcı Raporu',
    description:
      'Yatırımcı bazında seçilen ay için üretim özeti, günlük dağılım grafiği ve santral kırılımı.',
    path: '/report-api/investor-monthly',
    filename: 'aylik-yatirimci-raporu.pdf',
  },
  {
    id: 'plant-daily',
    title: 'Günlük Santral Raporu',
    description:
      'Seçilen yatırımcı ve santraller için günlük üretim özeti, son 7 gün grafiği ve santral kırılımı.',
    path: '/report-api/plant-daily',
    filename: 'gunluk-santral-raporu.pdf',
  },
  {
    id: 'inverter-daily',
    title: 'Günlük İnverter Raporu',
    description:
      'Seçilen yatırımcı ve santraller için günlük inverter üretim özeti, son 7 gün grafiği ve inverter kırılımı.',
    path: '/report-api/inverter-daily',
    filename: 'gunluk-inverter-raporu.pdf',
  },
  {
    id: 'plant-monthly',
    title: 'Aylık Santral Raporu',
    description:
      'Seçilen yatırımcı ve santraller için aylık üretim özeti, 12 aylık grafik ve santral kırılımı.',
    path: '/report-api/plant-monthly',
    filename: 'aylik-santral-raporu.pdf',
  },
  {
    id: 'plant-monthly-custom',
    title: 'Özel Aylık Santral Raporu',
    description:
      'Görsel tasarıma sadık kalınmış, dairesel verimlilik göstergeli özel formatlı rapor.',
    path: '/report-api/plant-monthly-custom',
    filename: 'ozel-aylik-rapor.pdf',
  },
  {
    id: 'plant-budget-vs-actual',
    title: 'Bütçe vs. Gerçekleşen Üretim',
    description:
      'Seçilen dönem ve santrallara göre hedef üretim (PVGIS/manuel) ile gerçekleşen üretimin aylık karşılaştırması.',
    path: '/report-api/plant-budget-vs-actual',
    filename: 'butce-vs-gerceklesen-uretim.pdf',
  },
];

/**
 * Report servisinden PDF blob URL döner (modal içinde iframe ile göstermek için).
 * Caller URL'i kullandıktan sonra URL.revokeObjectURL() ile temizlemelidir.
 */
export async function fetchPdfBlobUrl(report: PdfReportDef, options?: ReportOpenOptions): Promise<string> {
  return fetchPdfBlobUrlFromPath(report.path, options);
}

export async function fetchPdfBlobUrlByPath(path: string, options?: ReportOpenOptions): Promise<string> {
  return fetchPdfBlobUrlFromPath(path, options);
}

export async function sendReportByEmail(
  reportKey: string,
  email: string,
  options?: ReportOpenOptions,
  extraParams?: Record<string, string | number | boolean | null | undefined>,
): Promise<void> {
  const token = localStorage.getItem('token');
  const target = new URL(`/api/reports/send/${reportKey}`, window.location.origin);
  
  target.searchParams.set('email', email);
  if (options?.investorUuid) target.searchParams.set('investor_uuid', options.investorUuid);
  if (options?.investorName) target.searchParams.set('investor_name', options.investorName);
  if (options?.startDate) target.searchParams.set('start_date', options.startDate);
  if (options?.endDate) target.searchParams.set('end_date', options.endDate);
  if (options?.rangeLabel) target.searchParams.set('range_label', options.rangeLabel);
  if (options?.powerPlantUuids?.length) {
    target.searchParams.set('power_plant_uuids', options.powerPlantUuids.join(','));
  }

  if (extraParams) {
    Object.entries(extraParams).forEach(([k, v]) => {
      if (v !== null && v !== undefined && !target.searchParams.has(k)) {
        target.searchParams.set(k, String(v));
      }
    });
  }

  const res = await fetch(target.pathname + target.search, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (res.status === 401) {
    clearAuthSession();
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new Error('Oturum süresi doldu.');
  }
  if (!res.ok) {
    throw new Error('Rapor gonderilemedi');
  }
}

export async function executeReportSQLPreview(sql: string): Promise<any[]> {
  const token = localStorage.getItem('token');
  const res = await fetch('/api/reports/query-preview', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ sql }),
  });

  if (res.status === 401) {
    clearAuthSession();
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new Error('Oturum süresi doldu.');
  }

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.error || `SQL çalıştırılamadı (${res.status})`);
  }

  return data;
}

// ─── Rapor Zamanlamaları (Report Schedules) ───────────────────────────────────

export interface ReportSchedule {
  id: number;
  report_id: string;
  title: string;
  is_active: boolean;
  sql_query: string;
  cron_expression: string;
  created_at: string;
  updated_at: string;
}

export async function fetchReportSchedules(reportId: string): Promise<ReportSchedule[]> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reports/${reportId}/schedules`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Zamanlamalar getirilemedi');
  return res.json();
}

export async function createReportSchedule(reportId: string, payload: { title: string; sql_query: string; cron_expression: string; is_active: boolean }): Promise<ReportSchedule> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reports/${reportId}/schedules`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Zamanlama oluşturulamadı');
  return res.json();
}

export async function updateReportSchedule(id: number, payload: { title: string; sql_query: string; cron_expression: string; is_active: boolean }): Promise<ReportSchedule> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reports/schedules/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Zamanlama güncellenemedi');
  return res.json();
}

export async function fetchReportSettings<T = Record<string, unknown>>(reportId: string): Promise<ReportSettingsRecord<T>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reports/${reportId}/settings`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (res.status === 401) {
    clearAuthSession();
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new Error('Oturum süresi doldu.');
  }

  if (!res.ok) {
    throw new Error('Rapor ayarları getirilemedi');
  }

  return res.json();
}

export async function updateReportSettings<T = Record<string, unknown>>(reportId: string, settings_json: T): Promise<ReportSettingsRecord<T>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reports/${reportId}/settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ settings_json }),
  });

  if (res.status === 401) {
    clearAuthSession();
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new Error('Oturum süresi doldu.');
  }

  if (!res.ok) {
    throw new Error('Rapor ayarları kaydedilemedi');
  }

  return res.json();
}

export async function deleteReportSchedule(id: number): Promise<void> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reports/schedules/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Zamanlama silinemedi');
}
